import type { CheckboxChangeEvent, CheckboxRef } from 'beesoft-components';
import { Checkbox as BeeSoftCheckbox } from 'beesoft-components';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';
import useTestTag from '../../../common/hooks/use-test-tag';
import clsx from 'clsx';

export interface CheckboxChangeEventType {
  name?: string;
  value: string;
  checked: boolean;
}

export interface CheckboxProps extends DynamicFieldComponentProps<string, CheckboxChangeEventType> {
  initialChecked?: boolean;
  partial?: boolean;
  showError?: boolean;
  useAnimation?: boolean;
}

const CheckboxComponent = (props: CheckboxProps, ref: Ref<CheckboxRef>) => {
  const {
    name,
    label,
    value,
    initialChecked = false,
    partial = false,
    useAnimation = false,
    onChange,
    readOnly,
    required,
    error,
    disabled,
    showError = true,
  } = props;

  const [checkedState, setCheckedState] = useState(initialChecked);
  // ensure readOnly is always a boolean
  const isReadOnly = Boolean(readOnly) || Boolean(disabled);

  useEffect(() => {
    setCheckedState(initialChecked);
  }, [initialChecked]);

  const tagCreator = useTestTag(label || name || '');

  const handleOnChange = useCallback(
    (event?: CheckboxChangeEvent) => {
      if (event) {
        onChange?.({
          name: name || event.originalEvent?.target.name,
          value: value || (event.value as string),
          checked: event.checked,
        });

        setCheckedState(event.checked);
      }
    },
    [name, value, onChange]
  );

  const isRequiredIndicatorVisible = required && !readOnly;

  const checkboxWrapperStyles = clsx('odin-p-1', {
    'odin-flex odin-items-center odin-gap-2': isRequiredIndicatorVisible,
  });

  return (
    <>
      <div className={checkboxWrapperStyles} ref={tagCreator(checkedState.toString())}>
        <BeeSoftCheckbox
          ref={ref}
          name={name}
          value={value}
          label={label}
          checked={checkedState}
          partial={partial}
          useAnimation={useAnimation}
          readOnly={isReadOnly}
          onChange={handleOnChange}
          className={'odin-text-sm'}
        />
        <RequiredIndicator readOnly={isReadOnly} required={required} />
      </div>
      {error && showError && <ErrorLabel role="alert">{error}</ErrorLabel>}
    </>
  );
};

const Checkbox = forwardRef(CheckboxComponent);
export default Checkbox;
